// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MIT

import { Component, OnInit, OnDestroy, ViewChild, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  FormControl,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { BaseModalComponent, BaseModalConfig } from '@lfx/shared/components/base-modal';
import { ReportIssueService } from '@lfx/key-projects/services/report-issue.service';
import { ReportIssueEntity } from '@lfx/key-projects/entities/report-issue.entity';


@Component({
  selector: 'lfx-report-issue-modal',
  imports: [CommonModule, ReactiveFormsModule, BaseModalComponent],
  standalone: true,
  template: `
    <lfx-base-modal
      [config]="modalConfig"
      [isLoading]="isLoading"
      [errorMessage]="errorMessage"
      (confirmed)="onSubmit()">

      <form [formGroup]="reportForm" (ngSubmit)="onSubmit()">
        <div class="form-group mb-3">
          <label class="form-label" for="area">{{ config.fields.area.label }} <span class="required">*</span></label>
          <p class="form-help">{{ config.fields.area.helpText }}</p>
          <select
            id="area"
            class="form-select"
            [class.is-invalid]="!!getControlErrors(areaControl)"
            formControlName="area"
            aria-label="Select metric area">
            <option value="" disabled>Please select an area</option>
            <option *ngFor="let option of areaOptions; trackBy: trackByValue" [value]="option.value">
              {{ option.label }}
            </option>
          </select>
          <ng-container *ngIf="getControlErrors(areaControl) as errors">
            <div class="invalid-feedback" *ngIf="errors['required']">
              Please select an area.
            </div>
          </ng-container>
        </div>

        <!-- Description -->
        <div class="form-group mb-3">
          <label class="form-label" for="description">
            {{ config.fields.description.label }} <span class="required">*</span>
          </label>
          <p class="form-help">{{ config.fields.description.helpText }}</p>
          <textarea
            id="description"
            rows="3"
            class="form-control"
            [class.is-invalid]="!!getControlErrors(descriptionControl)"
            formControlName="description"
            ngbAutofocus
          ></textarea>
          <ng-container *ngIf="getControlErrors(descriptionControl) as errors">
            <div class="invalid-feedback" *ngIf="errors['whitespace']">
              Description is required.
            </div>
            <div class="invalid-feedback" *ngIf="errors['trimmedMinLength']">
              Description must be at least {{ config.validation.minDescriptionLength }} characters.
            </div>
            <div class="invalid-feedback" *ngIf="errors['trimmedMaxLength']">
              Description cannot exceed {{ config.validation.maxDescriptionLength }} characters.
            </div>
          </ng-container>
        </div>
      </form>
    </lfx-base-modal>
  `,
  styleUrls: ['./report-issue-modal.component.scss'],
})
export class ReportIssueModalComponent implements OnInit, OnDestroy {
  @ViewChild(BaseModalComponent) baseModal!: BaseModalComponent;

  @Input() metricType!: string;
  @Input() metricDisplayName!: string;
  @Input() projectSlug?: string;
  @Input() currentUrl?: string;

  reportForm!: FormGroup;
  isLoading = false;
  errorMessage = '';
  private destroy$ = new Subject<void>();

  protected readonly config = ReportIssueEntity.getConfig();

  protected areaOptions: { value: string; label: string }[] = [];

  modalConfig: BaseModalConfig = {
    title: this.config.display.title,
    confirmLabel: this.config.display.confirmLabel,
    cancelLabel: 'Cancel',
    confirmVariant: 'primary',
    allowBackdropClose: true,
    confirmDisabled: true,
  };

  constructor(
    private formBuilder: FormBuilder,
    private reportIssueService: ReportIssueService
  ) {}

  ngOnInit(): void {
    this.initializeAreaOptions();
    this.initializeForm();
  }

  private initializeAreaOptions(): void {
    const validTypes = ReportIssueEntity.getValidMetricTypes();
    this.areaOptions = validTypes.map(type => ({
      value: type,
      label: ReportIssueEntity.getMetricDisplayName(type)
    }));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.reportIssueService.resetState();
  }

  private initializeForm(): void {
    this.reportForm = this.formBuilder.group({
      area: [
        this.metricType || '',
        [Validators.required],
      ],
      description: [
        '',
        [
          ReportIssueService.notWhitespaceOnly,
          ReportIssueService.trimmedMinLength(this.config.validation.minDescriptionLength),
          ReportIssueService.trimmedMaxLength(this.config.validation.maxDescriptionLength),
        ],
      ],
    });

    this.reportForm.statusChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((status) => {
        this.modalConfig = {
          ...this.modalConfig,
          confirmDisabled: status === 'INVALID',
        };
      });
  }

  get areaControl(): FormControl {
    return this.reportForm.get('area') as FormControl;
  }

  get descriptionControl(): FormControl {
    return this.reportForm.get('description') as FormControl;
  }

  getControlErrors(control: FormControl): Record<string, unknown> | null {
    return control.invalid && (control.dirty || control.touched) ? control.errors : null;
  }

  trackByValue(_index: number, item: { value: string; label: string }): string {
    return item.value;
  }

  onSubmit(): void {
    if (this.reportForm.valid && !this.isLoading) {
      this.submitReport();
    }
  }

  private submitReport(): void {
    this.isLoading = true;
    this.errorMessage = '';
    this.reportForm.disable();

    const reportData = {
      area: this.areaControl.value,
      description: this.descriptionControl.value.trim(),
      url: this.currentUrl,
    };

    this.reportIssueService
      .submitReport(reportData)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.baseModal.closeWithResult(response);
        },
        error: (error) => {
          this.isLoading = false;
          this.reportForm.enable();
          this.errorMessage = error.message || this.config.display.errorMessage;
        },
      });
  }
}

